import { Info } from 'lucide-react';

import { cn } from '../../lib/cn';
import { Z } from '../../tokens/z-index';
import { Tooltip } from '../Tooltip/Tooltip';

export interface LabelHintProps {
  /** Explicação exibida no hover/focus. Consumer passa valor de t(). */
  hint: string;
  /** Lado preferido do tooltip. Default `top`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  className?: string;
}

/**
 * LabelHint — ícone "i" ao lado do texto de um label de formulário que exibe
 * uma explicação para o usuário em tooltip (hover/focus).
 *
 * Renderizado automaticamente pelos componentes de form de @alamo/ui que
 * expõem a prop `labelHint` (Input, NumberInputWithUnit). Para campos com
 * `<label>` manual, usar diretamente: `<LabelHint hint={t('...')} />`.
 *
 * O tooltip usa `Z.DROPDOWN_OVER_MODAL` porque hints aparecem com frequência
 * dentro de Dialogs (Z.MODAL = 400) e o trigger é hover-only — nunca fica
 * "aberto esquecido" atrás de um modal, então o tier alto é seguro também
 * fora de modais.
 */
export function LabelHint({ hint, side = 'top', className }: LabelHintProps) {
  // O NBSP cola o ícone à última palavra do label: line break é proibido em
  // volta de U+00A0, então o ícone nunca quebra sozinho pra linha seguinte.
  // Ele também substitui o espaçamento que antes vinha de `ml-1`.
  return (
    <span className="whitespace-nowrap">
      {'\u00A0'}
      <Tooltip
        content={hint}
        side={side}
        zIndex={Z.DROPDOWN_OVER_MODAL}
        className="max-w-xs font-body"
      >
        <button
          type="button"
          aria-label={hint}
          className={cn(
            'inline-flex shrink-0 cursor-help items-center align-middle',
            'text-slate-400 transition-colors hover:text-slate-600',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full',
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </button>
      </Tooltip>
    </span>
  );
}
